/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 * Modifications copyright (C) 2023 TechnoMindCorp. / Y.K.
 */

import './toolbarDropDown.css'

import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  createContext,
  useContext,
  useLayoutEffect,
} from 'react'
import { createPortal } from 'react-dom'
import { ToolbarButton } from './toolbarButton'

type DropDownContextType = {
  registerItem: (ref: React.RefObject<HTMLButtonElement>) => void
}

const DropDownContext = createContext<DropDownContextType | null>(null)

export function DropDownItem({
  children,
  className,
  onClick,
  title,
}: {
  children: React.ReactNode
  className: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  title?: string
}) {
  const ref = useRef<HTMLButtonElement>(null)

  const dropDownContext = useContext(DropDownContext)

  if (dropDownContext === null) {
    throw new Error('DropDownItem must be used within a DropDown')
  }

  const { registerItem } = dropDownContext

  useEffect(() => {
    if (ref && ref.current) {
      registerItem(ref)
    }
  }, [ref, registerItem])

  return (
    <button
      className={className}
      onClick={onClick}
      ref={ref}
      title={title}
      type="button"
    >
      {children}
    </button>
  )
}

function DropDownItems({
  children,
  dropDownRef,
  onClose,
}: {
  children: React.ReactNode
  dropDownRef: React.Ref<HTMLDivElement>
  onClose: () => void
}) {
  const [items, setItems] = useState<React.RefObject<HTMLButtonElement>[]>()
  const [highlightedItem, setHighlightedItem] =
    useState<React.RefObject<HTMLButtonElement>>()

  const registerItem = useCallback(
    (itemRef: React.RefObject<HTMLButtonElement>) => {
      setItems((prev) => (prev ? [...prev, itemRef] : [itemRef]))
    },
    [setItems]
  )

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!items) {
      return
    }

    const key = event.key

    if (['Escape', 'ArrowUp', 'ArrowDown', 'Tab'].includes(key)) {
      event.preventDefault()
    }

    if (key === 'Escape' || key === 'Tab') {
      onClose()
    } else if (key === 'ArrowUp') {
      setHighlightedItem((prev) => {
        if (!prev) {
          return items[0]
        }
        const index = items.indexOf(prev) - 1
        return items[index === -1 ? items.length - 1 : index]
      })
    } else if (key === 'ArrowDown') {
      setHighlightedItem((prev) => {
        if (!prev) {
          return items[0]
        }
        return items[items.indexOf(prev) + 1]
      })
    }
  }

  const contextValue = useMemo(
    () => ({
      registerItem,
    }),
    [registerItem]
  )

  useEffect(() => {
    if (items && !highlightedItem) {
      setHighlightedItem(items[0])
    }

    if (highlightedItem && highlightedItem.current) {
      highlightedItem.current.focus()
    }
  }, [items, highlightedItem])

  return (
    <DropDownContext.Provider value={contextValue}>
      <div className="dropdown" ref={dropDownRef} onKeyDown={handleKeyDown}>
        {children}
      </div>
    </DropDownContext.Provider>
  )
}

export function ToolbarDropDown({
  disabled = false,
  buttonLabel,
  buttonAriaLabel,
  icon,
  children,
  stopCloseOnClickSelf,
  showDropDownIcon,
  tooltip,
}: {
  disabled?: boolean
  buttonAriaLabel?: string
  icon?: React.ReactNode
  buttonLabel?: string
  children: ReactNode
  stopCloseOnClickSelf?: boolean
  showDropDownIcon?: boolean
  tooltip?: string
}): JSX.Element {
  const dropDownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [showDropDown, setShowDropDown] = useState(false)

  const handleClose = () => {
    setShowDropDown(false)
    if (buttonRef && buttonRef.current) {
      buttonRef.current.focus()
    }
  }

  // ツールバーのボタンクリック時に表示されるドロップダウンの表示位置を調整する
  // （useCrewEmojiPickerを参考にしている）
  useLayoutEffect(() => {
    if (dropDownRef.current && buttonRef.current) {
      // クリックされたツールバーボタンのRefを基準に表示位置を制御する
      const anchorRect = buttonRef.current.getBoundingClientRect()
      // 演算後の位置を設定するためのRefを取得する
      const dropDownDivRef = dropDownRef.current as HTMLDivElement

      // center align the tooltip by taking both the target and tooltip widths into account
      dropDownDivRef.style.left =
        anchorRect.left +
        anchorRect.width / 2 -
        dropDownDivRef.offsetWidth / 2 +
        'px'
      // make sure it doesn't poke off the left side of the page
      dropDownDivRef.style.left = Math.max(dropDownDivRef.offsetLeft, 0) + 'px'
      //or off the right
      dropDownDivRef.style.left =
        Math.min(
          dropDownDivRef.offsetLeft,
          document.body.clientWidth - dropDownDivRef.offsetWidth
        ) + 'px'

      // ツールバーのボタン位置が画面の上半分か下半分かで表示位置を変更する
      // 画面中央よりも上に位置していて下方向に表示する場合
      if (anchorRect.top < window.innerHeight / 2) {
        // ツールバーボタンの下位置から画面下端までの高さ
        const heightFromToolbarButtonBottomToWindowBottom =
          window.innerHeight - anchorRect.bottom

        // top位置をボタンの下端に合わせて表示すると画面外へ見切れる場合
        if (
          dropDownDivRef.offsetHeight >
          heightFromToolbarButtonBottomToWindowBottom
        ) {
          // top位置を基準に表示すると見切れてしまうため、bottom位置を基準に表示する

          // bottom位置を画面下端に固定する
          dropDownDivRef.style.bottom = '0px'
          // top位置を画面下端から表示対象の要素の高さを引いた位置(画面の高さ - 要素の高さ)に設定する
          dropDownDivRef.style.top =
            window.innerHeight - dropDownDivRef.offsetHeight + 'px'
        } else {
          // top位置をボタンの下端に設定する
          dropDownDivRef.style.top = anchorRect.bottom + 'px'
        }
      } else {
        // 画面中央よりも下に位置していて上方向に表示する場合

        // bottom位置をボタンの上端に合わせて表示すると画面外へ見切れる場合
        if (dropDownDivRef.offsetHeight > anchorRect.top) {
          // bottom位置を基準に表示すると見切れてしまうため、top位置を基準に表示する

          // top位置を画面上端に固定する
          dropDownDivRef.style.top = '0px'
          // bottom位置を画面上端から表示対象の要素を引いた高さ(画面の高さ - 要素の高さ)に設定する
          dropDownDivRef.style.bottom =
            window.innerHeight - dropDownDivRef.offsetHeight + 'px'
        } else {
          // bottom位置をボタンの上端に設定する
          dropDownDivRef.style.bottom =
            window.innerHeight - anchorRect.top + 'px'
        }
      }
    }
  }, [dropDownRef, buttonRef, showDropDown])

  useEffect(() => {
    const button = buttonRef.current

    if (button !== null && showDropDown) {
      const handle = (event: MouseEvent) => {
        const target = event.target
        if (stopCloseOnClickSelf) {
          if (
            dropDownRef.current &&
            dropDownRef.current.contains(target as Node)
          ) {
            return
          }
        }
        if (!button.contains(target as Node)) {
          setShowDropDown(false)
        }
      }
      document.addEventListener('click', handle)

      return () => {
        document.removeEventListener('click', handle)
      }
    }
  }, [dropDownRef, buttonRef, showDropDown, stopCloseOnClickSelf])

  return (
    <>
      <ToolbarButton
        disabled={disabled}
        icon={icon}
        aria-label={buttonAriaLabel || buttonLabel}
        onHandleButtonClick={() => setShowDropDown(!showDropDown)}
        ref={buttonRef}
        buttonLabel={buttonLabel}
        showDropDownIcon={showDropDownIcon}
        tooltip={tooltip}
      />

      {showDropDown &&
        createPortal(
          <DropDownItems dropDownRef={dropDownRef} onClose={handleClose}>
            {children}
          </DropDownItems>,
          document.body
        )}
    </>
  )
}
